import { useHeaderOverlay } from '../lib/HeaderOverlayContext';
import { navigationBottom } from '../lib/overlayPosition';
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Loading } from './Loading';

const AskStarmapPanel = lazy(() => import('./AskStarmapPanel').then(module => ({ default: module.AskStarmapPanel })));
const SuggestionModal = lazy(() => import('./SuggestionModal').then(module => ({ default: module.SuggestionModal })));

export function AskStarmapLauncher() {
  const [open, setOpen] = useHeaderOverlay('ask');
  const [panelTop, setPanelTop] = useState(0);
  const [suggestOpen, setSuggestOpen] = useHeaderOverlay('suggestion');
  const [mobileActionsOpen, setMobileActionsOpen] = useHeaderOverlay('actions');
  const [headerHost, setHeaderHost] = useState<HTMLElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileTriggerRef = useRef<HTMLButtonElement>(null);
  const firstMobileActionRef = useRef<HTMLButtonElement>(null);
  const askReturnFocusRef = useRef<HTMLElement | null>(null);
  const suggestReturnFocusRef = useRef<HTMLElement | null>(null);
  const location = useLocation();
  const onAskPage = /^\/app\/[^/]+\/ask(?:\/|$)/.test(location.pathname) || location.pathname === '/ask';

  const restoreFocus = useCallback((target: HTMLElement | null) => {
    requestAnimationFrame(() => target?.focus());
  }, []);

  const closeAsk = useCallback(() => {
    setOpen(false);
    restoreFocus(askReturnFocusRef.current);
  }, [restoreFocus, setOpen]);

  const closeSuggestion = useCallback(() => {
    setSuggestOpen(false);
    restoreFocus(suggestReturnFocusRef.current);
  }, [restoreFocus, setSuggestOpen]);

  useEffect(() => {
    setHeaderHost(document.querySelector<HTMLElement>('.hdr-r'));
  }, []);

  useEffect(() => {
    if (!open && !mobileActionsOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (mobileActionsOpen) {
        setMobileActionsOpen(false);
        mobileTriggerRef.current?.focus();
        return;
      }
      closeAsk();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closeAsk, mobileActionsOpen, open, setMobileActionsOpen]);

  useEffect(() => {
    if (!mobileActionsOpen) return;
    firstMobileActionRef.current?.focus();

    const onPointerDown = (event: PointerEvent) => {
      if (!mobileMenuRef.current?.contains(event.target as Node)) setMobileActionsOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [mobileActionsOpen, setMobileActionsOpen]);

  useEffect(() => {
    setOpen(false);
    setMobileActionsOpen(false);
  }, [location.pathname, setOpen, setMobileActionsOpen]);

  useEffect(() => {
    if (!open) return;
    const update = () => setPanelTop(navigationBottom() + 12);
    update();
    const header = document.querySelector('header.sticky');
    const observer = new ResizeObserver(update);
    if (header) observer.observe(header);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, { passive: true });
    return () => { observer.disconnect(); window.removeEventListener('resize', update); window.removeEventListener('scroll', update); };
  }, [open]);

  return (
    <>
      {open && (
        <div
          role="dialog"
          aria-label="Ask Starmap"
          aria-modal="false"
          style={{ top: panelTop, '--ask-height': `min(620px, calc(100dvh - ${panelTop + 16}px))` } as CSSProperties}
          className="ask-layer fixed right-4 z-[210] w-[min(420px,calc(100vw-32px))] overflow-hidden rounded-2xl border border-[var(--color-brand-line)] bg-[var(--color-brand-surface)] shadow-[-12px_12px_40px_rgba(0,0,0,0.28)] sm:right-6"
        >
          <Suspense fallback={<Loading />}><AskStarmapPanel mode="floating" autoFocus onClose={closeAsk} /></Suspense>
        </div>
      )}

      {headerHost && createPortal(
        <>
          <button
            type="button"
            onClick={(event) => {
              suggestReturnFocusRef.current = event.currentTarget;
              setSuggestOpen(true);
            }}
            aria-label="Suggest a Starmap improvement"
            className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/12 bg-white/[0.06] text-white/62 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-green)]/70 min-[701px]:inline-flex"
          >
            <BugIcon />
          </button>
          <button
            type="button"
            onClick={(event) => {
              askReturnFocusRef.current = event.currentTarget;
              if (onAskPage) {
                document.querySelector<HTMLTextAreaElement>('.ask-page textarea')?.focus();
              } else if (open) {
                closeAsk();
              } else {
                setOpen(true);
              }
            }}
            aria-label={open ? 'Close Ask Starmap' : 'Open Ask Starmap'}
            aria-expanded={open}
            aria-pressed={onAskPage || open}
            className="hidden h-11 min-w-11 shrink-0 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3 text-sm font-medium text-white transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-green)]/70 min-[701px]:inline-flex aria-pressed:bg-white/15 aria-pressed:border-white/40"
          >
            <ChatIcon />
            <span className="hidden min-[1180px]:inline">Ask Starmap</span>
          </button>
          <div ref={mobileMenuRef} className="relative min-[701px]:hidden">
            <button
              ref={mobileTriggerRef}
              type="button"
              onClick={() => setMobileActionsOpen((value) => !value)}
              aria-label={mobileActionsOpen ? 'Close Starmap actions' : 'Open Starmap actions'}
              aria-expanded={mobileActionsOpen}
              aria-controls="starmap-mobile-actions"
              aria-pressed={onAskPage || open}
              className="inline-flex h-[38px] w-[38px] items-center justify-center rounded-md border border-white/15 bg-white/[0.06] text-white/78 hover:bg-white/12 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/55"
            >
              <ChatIcon />
            </button>

            {mobileActionsOpen && (
              <div
                id="starmap-mobile-actions"
                role="menu"
                aria-label="Starmap actions"
                className="absolute right-0 top-[calc(100%+0.5rem)] z-[220] w-56 overflow-hidden rounded-md border border-white/15 bg-[#0B1220] p-1.5 text-white shadow-[0_18px_48px_rgba(0,0,0,0.42)]"
              >
                <button
                  ref={firstMobileActionRef}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMobileActionsOpen(false);
                    askReturnFocusRef.current = mobileTriggerRef.current;
                    if (onAskPage) {
                      document.querySelector<HTMLTextAreaElement>('.ask-page textarea')?.focus();
                    } else if (open) {
                      closeAsk();
                    } else {
                      setOpen(true);
                    }
                  }}
                  className="flex min-h-11 w-full items-center gap-3 rounded px-3 py-2 text-left text-sm text-white/78 hover:bg-white/10 hover:text-white focus:bg-white/10 focus:outline-none"
                >
                  <ChatIcon />
                  <span>{open ? 'Close Ask Starmap' : 'Ask Starmap'}</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMobileActionsOpen(false);
                    suggestReturnFocusRef.current = mobileTriggerRef.current;
                    setSuggestOpen(true);
                  }}
                  className="flex min-h-11 w-full items-center gap-3 rounded px-3 py-2 text-left text-sm text-white/78 hover:bg-white/10 hover:text-white focus:bg-white/10 focus:outline-none"
                >
                  <BugIcon />
                  <span>Suggest improvement</span>
                </button>
              </div>
            )}
          </div>
        </>,
        headerHost,
      )}

      {suggestOpen && <Suspense fallback={<Loading />}><SuggestionModal onClose={closeSuggestion} /></Suspense>}
    </>
  );
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
      <path
        d="M5.6 17.2A7.5 7.5 0 1 1 8 19.05l-3.15.75.75-2.6Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M8.7 11.6h6.6M8.7 8.7h4.8"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BugIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <path
        d="M9 8.2h6M9.2 4.8 11 7.7M14.8 4.8 13 7.7M5 10.8h3M16 10.8h3M5 15h3M16 15h3M8.3 8.6a5.1 5.1 0 0 1 7.4 0v5.7a3.7 3.7 0 0 1-7.4 0V8.6Z"
        stroke="currentColor"
        strokeWidth="1.55"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 9v8" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
    </svg>
  );
}
